import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { Contact } from "@/api/queries";
import { useAddContactChannels } from "@/api/queries";
import { Dialog } from "@/components/Dialog";
import { Button } from "@/components/ui";
import { formatPhone } from "@/lib/format";
import {
  EmailList,
  PhoneList,
  emailRows,
  emailsBody,
  phoneRows,
  phonesBody,
} from "./ContactChannels";

/**
 * *Agregar teléfono o correo* — what a seller may do to a contact that exists.
 *
 * A person's record is management's to change, at a seller's request. What a
 * seller does on their own is add another way to reach them: it is learned on
 * a call and lost if it waits for someone else to type it. So this is the two
 * lists of design screen 07 and nothing else, starting blank — what the
 * contact already has is shown above them to read, not to edit.
 */
export function AgregarCanales({
  onClose,
  companyId,
  contact,
}: {
  onClose: () => void;
  companyId: number;
  contact: Contact;
}) {
  const { t } = useTranslation();
  // No argument: a blank row each, never the contact's own rows. Rows carrying
  // an id are edits, and the endpoint refuses them.
  const [phones, setPhones] = useState(() => phoneRows());
  const [emails, setEmails] = useState(() => emailRows());
  const [error, setError] = useState<string | null>(null);
  const add = useAddContactChannels(companyId);

  async function save() {
    const body = { phones: phonesBody(phones), emails: emailsBody(emails) };
    if (body.phones.length === 0 && body.emails.length === 0) {
      setError(t("addChannels.nothing"));
      return;
    }
    setError(null);
    try {
      await add.mutateAsync({ id: contact.id, body });
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : t("errors.saveContact"));
    }
  }

  const existing = [
    ...contact.phones.map((phone) => formatPhone(phone.phone)),
    ...contact.emails.map((email) => email.email),
  ];

  return (
    <Dialog
      open
      onClose={onClose}
      kicker={contact.name}
      title={t("addChannels.title")}
      footer={
        <>
          <Button
            variant="primary"
            disabled={add.isPending}
            onClick={() => void save()}
          >
            {add.isPending ? t("newContact.saving") : t("addChannels.save")}
          </Button>
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </>
      }
    >
      {error ? (
        <p role="alert" className="mb-4 border-2 border-brand px-3 py-2 text-sm">
          {error}
        </p>
      ) : null}

      <p className="mb-5 text-[13px] text-mute-600">{t("addChannels.lead")}</p>

      {existing.length > 0 ? (
        <div className="mb-5">
          <p className="kicker mb-1 text-mute-600">{t("addChannels.existing")}</p>
          <ul className="text-sm">
            {existing.map((value) => (
              <li key={value} className="break-all">
                {value}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="grid gap-6">
        <PhoneList rows={phones} onChange={setPhones} />
        <EmailList rows={emails} onChange={setEmails} />
      </div>
    </Dialog>
  );
}
